import { useId, type InputHTMLAttributes, type Ref } from 'react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  /** Shown beneath the field. Phrased for a child, not a developer. */
  error?: string
  hint?: string
  /** React 19 passes ref as an ordinary prop; forwardRef is no longer needed. */
  ref?: Ref<HTMLInputElement>
}

export function Input({ label, error, hint, id, ref, ...props }: InputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="font-display text-ink text-(length:--text-small)">
        {label}
      </label>

      <input
        {...props}
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        className={[
          'bg-surface-raised text-ink rounded-(--radius-control) border-2 px-4',
          'min-h-(--size-tap) w-full',
          error ? 'border-coral' : 'border-border',
          props.className ?? '',
        ].join(' ')}
      />

      {hint ? (
        <p id={hintId} className="text-ink-muted text-(length:--text-small)">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-coral text-(length:--text-small)">
          {error}
        </p>
      ) : null}
    </div>
  )
}
